<template>
  <div :class="$style.page">
    <div :class="[$style.page__list, $style.list]">
      <div :class="$style.header">
        <h1 :class="[$style.title]">メッセージリスト</h1>
        <DolphinSelector
          v-if="selectorOptions.length > 1"
          :options="selectorOptions"
          v-model="selectorValue"
          small
          round
        />
      </div>

      <div :class="$style.list__actions">
        <ChatRoomListFilter v-if="shouldShowFilter" v-model="filterInput" />
        <div :class="[$style.bulkActions]">
          <FlatButton
            :class="$style.bulkActions__sendBulkButton"
            @click="isOpenBulkMessageSendModal = true"
            size="small"
            :disabled="checkedMatches.length === 0"
          >
            {{ bulkMessageSendLabel }}
          </FlatButton>
        </div>
      </div>

      <div :class="[$style.list__scrollTop, $style.scrollTop]">
        <FadeTransition>
          <button
            v-if="shouldShowScrollToTopButton"
            :class="[$style.scrollTop__button, $style.scrollTopButton]"
            @click="scrollSelectorsToTop"
          >
            <IconArrowUpward
              :class="$style.scrollTopButton__icon"
            />未読を確認する
          </button>
        </FadeTransition>
      </div>

      <div v-if="isShownBulkCheck" :class="$style.bulkCheckWrapper">
        <DolphinCheckbox
          :model-value="isAllChecked"
          @update:model-value="onBulkCheckedChanged"
          @click.stop
        />
        <p>一括でチェック</p>
      </div>

      <VerticalScrollEdgeDetector
        :class="[$style.list__selector, $style.selector]"
        @topStateChange="onChatListScrollToTopChanged($event.onTop)"
        @bottomStateChange="onChatListScrollToBottomChanged($event.onBottom)"
        ref="wrapperRef"
      >
        <FadeTransition>
          <div :class="$style.message" v-if="error">
            {{ error }}
          </div>
          <div :class="$style.message" v-else-if="data == null">
            <LoadingIndicator />
          </div>
          <div :class="$style.message" v-else-if="data.length === 0">
            <p :class="$style.message__text">
              表示可能なチャットは<br />ありません
            </p>
          </div>
          <MatchSelector
            v-else
            :items="selectorList"
            :activeRoomId="selectedRoomId"
            :showPlanner="isMemberRoute"
            :isLoadingNextPage="isLoadingNext"
            :checkable="activeView === 'own'"
            @select="_selectMatch"
            @changeCheck="(match, checked) => checkMatch(match.id, checked)"
          />
        </FadeTransition>
      </VerticalScrollEdgeDetector>
    </div>
    <router-view
      v-if="selectedRoomId !== null"
      :class="$style.page__detail"
      v-model:loading="loading"
      @onUpdateChatRoom="onUpdateChatRoom"
      @onUpdatedRole="onUpdatedRole"
    />
    <div v-else :class="[$style.default, $style.page__detail]">
      <OkanekoChatFp :class="$style.default__image" />
      <p :class="$style.default__body">チャットが選択されていません</p>
      <FlatButton
        type="submit"
        :class="$style.default__button"
        variation="primary"
        @click="openChatTemplateModal"
      >
        テンプレートの編集
      </FlatButton>
      <ChatTemplateModal>
        <ChatMessageTemplateModal
          @close="closeChatTemplateModal"
          disableInsert
        />
      </ChatTemplateModal>
    </div>
    <BulkMessageSendModal
      :open="isOpenBulkMessageSendModal"
      :matches="checkedMatches"
      :hasLineActivationProvider="hasLineActivationProvider"
      @sended="onBulkMessageSended"
      @close="isOpenBulkMessageSendModal = false"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'

import FadeTransition from '@/components/transitions/FadeTransition.vue'
import LoadingIndicator from '@/components/atoms/LoadingIndicator.vue'
import MatchSelector from '@/components/organisms/MatchSelector/MatchSelector.vue'
import ChatRoomListFilter from './components/ChatRoomListFilter/ChatRoomListFilter.vue'
import VerticalScrollEdgeDetector from '@/components/molecules/VerticalScrollEdgeDetector.vue'
import DolphinSelector from '@/components/atoms/DolphinSelector.vue'
import OkanekoChatFp from '@/assets/images/okaneko-chatfp.svg?component'
import ChatMessageTemplateModal from './components/ChatMessageTemplateModal/ChatMessageTemplateModal.vue'
import IconArrowUpward from '@/assets/images/icons/fill/arrow-upward.svg?component'
import BulkMessageSendModal from './components/BulkMessageSendModal/BulkMessageSendModal.vue'

import { useChatPageRouteControl } from './hooks/list/useChatPageRouteControl'
import { useChatList } from './hooks/list/useChatList'
import { useSelectFirst } from './hooks/list/useSelectFirst'
import { useMatchList } from './hooks/list/useMatchList/useMatchList'
import { useChatListGroupViewSelector } from './hooks/list/useChatListGroupViewSelector'
import { useModal } from '@/hooks/useModal'
import { useAlert } from '@/libs/alertPortal'
import FlatButton from '@/components/atoms/FlatButton.vue'
import DolphinCheckbox from '@/components/atoms/DolphinCheckbox.vue'
import type { MatchEntrance } from '@/types/app'
import { wait } from '@/libs/utilities/wait'
import type { ChatPageUiControlFragment } from './ChatPageContainer.generated'
import { useEntranceGUIDParam } from '@/features/entrance/useEntranceGUIDParam'
import { useMaybeRoutes } from '@/features/router/route'

// 初期値falseにしてしまうと、router-view内のmatchが読み込まれるまで切り替え放題になってしまい、
// いろいろぶっこわれるので初期画面表示するまでは _selectMatch の想定するように遷移をロックする
const loading = ref(true)

const props = defineProps<{
  viewerId: string
  uiControl: ChatPageUiControlFragment
  hasLineActivationProvider: boolean
}>()

const maybeChildRoute = useMaybeRoutes([
  'memberChat',
  'ownChat',
  'assignedChat',
])

const roomIdParam = computed((): Maybe<number> => {
  if (maybeChildRoute.value === undefined) {
    return undefined
  }

  return Number.parseInt(maybeChildRoute.value.params.roomId)
})

const { selectedRoomId, isMemberRoute, activeView, selectMatch } =
  useChatPageRouteControl(roomIdParam)

const _selectMatch = (match: MatchEntrance) => {
  // 前のAPIを完了を待ってから次のリクエストを発火させるみたいなことをやろうと思ったけど、
  // 関連するAPIコールが多くて大変なので、loading中は選択できないようにする形で解決させた
  // FIXME: loadingのインタラクション出してあげた方が良い
  if (!loading.value) {
    selectMatch(match)
  }
}

const { entranceGUID } = useEntranceGUIDParam({ must: true })

const {
  filterInput,
  data,
  error,
  isLoadingNext,
  hasNextPage,
  fetchNext,
  onUpdateChatRoom,
  mutate,
} = useChatList(props.viewerId, entranceGUID.value, {
  activeView,
})
const { selectorOptions, selectorValue } = useChatListGroupViewSelector(
  props.uiControl
)

const selectedMatchId = computed(
  () => data.value?.find(match => match.roomId === selectedRoomId.value)?.id
)

const { selectorList, matchList, checkMatch, changeBulkChecked } = useMatchList(
  {
    selectedMatchId,
    matches: data,
  }
)
const alert = useAlert()

const onUpdatedRole = async () => {
  // レプリカラグのため待つ
  await wait(100)
  mutate.value()
}

// ここから 一括送信 用の処理
const isShownBulkCheck = computed(() => {
  return data.value && data.value.length > 0
})

const isOpenBulkMessageSendModal = ref(false)

const checkedMatches = computed(() =>
  selectorList.value
    .filter(item => item.checked)
    .map(item => ({
      ...item.match,
    }))
)

const isAllChecked = computed(() => {
  return selectorList.value.every(item => item.checked)
})

const onBulkCheckedChanged = (checked: boolean) => {
  changeBulkChecked(checked)
}

watch(filterInput, () => {
  changeBulkChecked(false)
})

const bulkMessageSendLabel = computed(
  () => `一括送信メッセージの作成（${checkedMatches.value.length}件）`
)

const onBulkMessageSended = () => {
  alert.success('メッセージが送信されました')
  isOpenBulkMessageSendModal.value = false

  // 一括送信したあと、チャットリストを更新する
  // void mutate.value()

  // チェックボックスの用途が一括送信のみなので、Sended からは他にすることがないということでチェックを外す
  // チェックボックスの用途が2つ以上になれば、チェック状態をそのままにすることを検討してもよい
  changeBulkChecked(false)
}
// ここまで 一括送信 用の処理

useSelectFirst(matchList, selectMatch)

const { openChatTemplateModal, closeChatTemplateModal, ChatTemplateModal } =
  useModal('chatTemplate')

const wrapperRef = ref<{ wrapperRef: HTMLElement | null } | null>(null)
const scrollSelectorsToTop = () => {
  wrapperRef.value?.wrapperRef?.scrollTo({
    top: 0,
    behavior: 'smooth',
  })
}
const isChatListScrolledToTop = ref(false)
const onChatListScrollToTopChanged = (onTop: boolean) => {
  isChatListScrolledToTop.value = onTop
}
const shouldShowScrollToTopButton = computed(
  () =>
    !isChatListScrolledToTop.value &&
    matchList.value.some(match => match.latestMessage?.isUnread)
)

const onChatListScrollToBottomChanged = async (onBottom: boolean) => {
  if (onBottom && !isLoadingNext.value && hasNextPage.value) {
    await fetchNext()
  }
}

const shouldShowFilter = computed(
  () => activeView.value === 'own' || activeView.value === 'assigned'
)
</script>

<style lang="scss" module>
.page {
  display: flex;
  height: 100%;
  box-sizing: border-box;
  margin: 0 auto;

  &__list {
    z-index: 1;
    width: 340px;
    flex: 0 0 auto;
    border-right: 1px solid $color-border-low-emphasis;
    background-color: $color-surface-primary;
    isolation: isolate;
  }

  &__detail {
    flex: 1;
  }
}

.default {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  &__image {
    width: 200px;
  }

  &__body {
    color: $color-text-and-object-low-emphasis;
    font-size: 18px;
    font-weight: bold;
    text-align: center;
    margin-top: 16px;
  }

  &__button {
    margin-top: 18px;
  }
}

.list {
  display: flex;
  flex-direction: column;
  isolation: isolate;

  &__actions {
    padding: 12px;
    border-bottom: 1px solid $color-border-low-emphasis;

    > *:nth-child(n + 2) {
      margin-top: 12px;
    }
  }

  &__scrollTop {
    z-index: 2;
  }

  &__selector {
    z-index: 1;
  }
}

.scrollTop {
  position: relative;

  &__button {
    position: absolute;
    top: 8px;
    left: 50%;
    transform: translateX(-50%);
    width: 160px;
  }
}

.scrollTopButton {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 4px 12px;
  border-radius: 100vw;
  background-color: $color-surface-accent-red;
  color: $color-text-and-object-high-emphasis-inverse;
  box-shadow: $shadow-8;
  transition: filter opacity $transition-interaction;

  &:hover {
    filter: brightness(1.1);
  }

  &__icon {
    width: 1.2em;
    height: 1.2em;
    fill: currentcolor;
    color: inherit;
    transform: translateY(-1px);
  }
}

.selector {
  overflow: auto;
  height: 100%;
}

.header {
  display: flex;
  height: 52px;
  align-items: center;
  justify-content: space-between;
  padding: 12px;
  border-bottom: 1px solid $color-border-low-emphasis;
}

.title {
  font-size: 14px;
}

.bulkActions {
  &__sendBulkButton {
    width: 100%;
  }
}

.bulkCheckWrapper {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-bottom: 1px solid $color-border-low-emphasis;
}

.message {
  display: flex;
  height: 100%;
  align-items: center;
  justify-content: center;

  &__text {
    color: $color-text-and-object-low-emphasis;
    font-size: 14px;
    font-weight: bold;
    text-align: center;
  }
}
</style>
